import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Coffee, ExternalLink, Sparkles } from "lucide-react";
import { createCheckout } from "../lib/payments";

// Full-page paywall shown in place of a locked chapter.
export function PaywallGate({ chapter }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onUnlock = async () => {
    setBusy(true);
    setErr("");
    try {
      const { url } = await createCheckout("unlock");
      window.location.href = url;
    } catch (e) {
      setErr(e.message || "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="book-page ring-neon max-w-2xl mx-auto px-6 sm:px-12 py-14 text-center relative overflow-hidden"
      data-testid="paywall-gate"
    >
      <div className="flex justify-center mb-6">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center bg-slate-900/80 border border-cyan-400/50"
          style={{ boxShadow: "0 0 30px rgba(0,240,255,0.35)" }}
        >
          <Lock size={28} className="text-cyan-300" />
        </div>
      </div>

      <div className="text-xs uppercase tracking-[0.35em] text-cyan-300/80 mb-3 font-heading">
        {chapter?.number || "Chapter"} · Premium
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white mb-3 leading-tight">
        You&apos;ve reached the paid part of the book.
      </h2>
      <p className="font-body text-slate-300 text-base sm:text-lg leading-relaxed mb-2">
        Chapters 1–4 are the free preview. The rest — including{" "}
        <span className="neon-cyan italic">&ldquo;{chapter?.title}&rdquo;</span> — unlock in one go for a single, tiny price.
      </p>
      <p className="font-body text-slate-400 text-sm mb-8">
        One-time payment. Yours forever on this browser. No account. No subscription.
      </p>

      <motion.button
        whileHover={{ scale: 1.03, boxShadow: "0 0 32px rgba(0,240,255,0.5)" }}
        whileTap={{ scale: 0.97 }}
        onClick={onUnlock}
        disabled={busy}
        data-testid="paywall-unlock-btn"
        className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-heading font-semibold text-base sm:text-lg disabled:opacity-60"
      >
        <Sparkles size={18} />
        {busy ? "Opening checkout…" : "Unlock the full book — $5"}
      </motion.button>

      {err && (
        <div className="mt-4 text-sm text-rose-300" data-testid="paywall-error">{err}</div>
      )}

      <div className="mt-10 pt-8 border-t border-cyan-400/15 text-xs text-slate-500 font-heading tracking-widest uppercase">
        Secure checkout by Stripe
      </div>
    </motion.div>
  );
}

// A gentle inline strip shown across the reader with a tip jar + link to other AMC products.
export function SupportStrip() {
  const [busyPkg, setBusyPkg] = useState(null);
  const [customAmt, setCustomAmt] = useState("");
  const [err, setErr] = useState("");

  const go = async (pkgId, custom = null) => {
    setBusyPkg(pkgId);
    setErr("");
    try {
      const { url } = await createCheckout(pkgId, custom);
      window.location.href = url;
    } catch (e) {
      setErr(e.message || "Something went wrong.");
      setBusyPkg(null);
    }
  };

  const submitCustom = (e) => {
    e.preventDefault();
    const n = parseFloat(customAmt);
    if (!Number.isFinite(n) || n < 1) {
      setErr("Enter any whole dollar amount from $1 up.");
      return;
    }
    go("custom", n);
  };

  return (
    <div className="max-w-3xl mx-auto mt-10 mb-6 px-4" data-testid="support-strip">
      <div className="rounded-2xl border border-cyan-400/15 bg-slate-900/40 backdrop-blur px-5 py-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Coffee size={18} className="text-amber-300 shrink-0" />
          <div className="text-sm text-slate-300 font-body leading-snug">
            <span className="text-slate-100 font-medium">Enjoying the book?</span>{" "}
            <span className="text-slate-400">Toss AMC anything — even a buck helps fund the full 200-page edition.</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: "tip3", label: "$3" },
            { id: "tip5", label: "$5" },
            { id: "tip10", label: "$10" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => go(t.id)}
              disabled={busyPkg !== null}
              data-testid={`tip-${t.id}`}
              className="px-3.5 h-9 rounded-full text-sm font-heading font-semibold text-cyan-100 border border-cyan-400/30 hover:border-cyan-300 hover:text-white hover:bg-cyan-400/10 disabled:opacity-50 transition-colors"
            >
              {busyPkg === t.id ? "…" : t.label}
            </button>
          ))}
          <form onSubmit={submitCustom} className="flex items-center gap-1.5">
            <span className="text-slate-400 text-sm">$</span>
            <input
              type="number"
              min="1"
              step="1"
              placeholder="any"
              value={customAmt}
              onChange={(e) => setCustomAmt(e.target.value)}
              data-testid="tip-custom-input"
              className="w-20 h-9 rounded-full bg-slate-800/70 border border-slate-700 focus:border-cyan-400 focus:outline-none px-3 text-slate-100 font-mono text-sm"
            />
            <button
              type="submit"
              disabled={busyPkg !== null}
              data-testid="tip-custom-btn"
              className="px-3.5 h-9 rounded-full text-sm font-heading font-semibold bg-cyan-400/15 text-cyan-100 border border-cyan-400/40 hover:bg-cyan-400/25 disabled:opacity-50"
            >
              {busyPkg === "custom" ? "…" : "Give"}
            </button>
          </form>
        </div>

        <div className="flex items-start gap-3 pt-3 border-t border-cyan-400/10">
          <Sparkles size={16} className="text-violet-300 shrink-0 mt-0.5" />
          <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <div className="text-slate-300">
              <span className="text-slate-100 font-medium">Complete Edition</span>{" "}
              <span className="text-slate-400">— 150–200 pages, illustrations, deeper chapters.</span>{" "}
              <span className="text-cyan-200 font-mono text-xs">Pre-sale $15</span>
              <span className="text-slate-500 text-xs"> (will be $30 at launch)</span>
            </div>
            <button
              onClick={() => go("pre_edition")}
              disabled={busyPkg !== null}
              data-testid="preorder-btn"
              className="px-4 h-9 rounded-full text-sm font-heading font-semibold bg-gradient-to-r from-violet-500 to-cyan-400 text-slate-950 disabled:opacity-50"
            >
              {busyPkg === "pre_edition" ? "…" : "Pre-order $15"}
            </button>
          </div>
        </div>

        {err && (
          <div className="text-sm text-rose-300 -mt-1" data-testid="support-error">{err}</div>
        )}
      </div>

      <a
        href="https://amcinnovate-tfnn9y4c.manus.space"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="other-products-link"
        className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-slate-400 hover:text-cyan-200 font-heading"
      >
        Explore other products by AMC inNOVAtions <ExternalLink size={12} />
      </a>
    </div>
  );
}
